import { Ionicons } from "@expo/vector-icons";
import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import tw from "twrnc";

import { t } from "../../../i18n/translations";
import { supabase } from "../../../shared/services/supabaseService";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { useRoomReset } from "../hooks/useRoomReset";
import { resolveRoomGroupMeta } from "../utils/groupMeta";

import PlayerStatusList from "./PlayerStatusList";

const IQES = {
  background: "#090317",
  panel: "#0B1026",
  panelDeep: "#070A18",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  goldMuted: "#8A681E",
  purple: "#4B1F78",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  borderStrong: "rgba(248, 200, 78, 0.72)",
  green: "#7FE08E",
  red: "#EF6B63",
};

type WaitingRoomContext = {
  family_name?: string | null;
  friends_group_name?: string | null;
  friends_group_id?: string | null;
  name?: string | null;
};

interface MultiplayerWaitingForResultsProps {
  score: number;
  totalQuestions: number;
  percentCorrect?: number; // If not provided, will be calculated
  onReviewAnswers: () => void;
  onGoHome: () => void;
  roomId: string;
  isHost: boolean;
}

const MultiplayerWaitingForResults: React.FC<
  MultiplayerWaitingForResultsProps
> = ({
  score,
  totalQuestions,
  percentCorrect,
  onReviewAnswers,
  onGoHome,
  roomId,
  isHost,
}) => {
  console.log(
    "[MultiplayerWaitingForResults] Rendered intermediate waiting screen",
    {
      score,
      totalQuestions,
      percentCorrect,
    },
  );
  const percent =
    percentCorrect !== undefined
      ? percentCorrect
      : totalQuestions > 0
        ? Math.round((score / totalQuestions) * 100)
        : 0;

  const { resetRoom, resetting, resetError } = useRoomReset(roomId);
  const { language } = useLanguageStore();
  const [roomContextLabel, setRoomContextLabel] = React.useState<string | null>(null);
  const [allPlayersFinished, setAllPlayersFinished] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const loadRoomContext = async () => {
      const { data } = await supabase
        .from("multiplayer_game_rooms")
        .select("family_id, family_name, friends_group_id, friends_group_name, name, status")
        .eq("id", roomId)
        .maybeSingle();

      if (!active || !data) return;
      const meta = resolveRoomGroupMeta(data as WaitingRoomContext as any, language);
      setRoomContextLabel(meta.roomTitle);
    };
    loadRoomContext();
    return () => {
      active = false;
    };
  }, [language, roomId]);

  React.useEffect(() => {
    let active = true;

    const checkCompletion = async () => {
      const [{ data: room }, { data: players }] = await Promise.all([
        supabase
          .from("multiplayer_game_rooms")
          .select("status, current_session_id, family_id, family_name, friends_group_id, friends_group_name")
          .eq("id", roomId)
          .maybeSingle(),
        supabase
          .from("multiplayer_game_room_players")
          .select("finished_at")
          .eq("room_id", roomId),
      ]);

      if (!active) return;

      const totalPlayers = players?.length ?? 0;
      const finishedPlayers = players?.filter((player: { finished_at?: string | null }) => player.finished_at).length ?? 0;
      const everyoneFinished = totalPlayers > 0 && finishedPlayers >= totalPlayers;
      setAllPlayersFinished(everyoneFinished);

      if (room?.status === "completed" || everyoneFinished) {
        setAllPlayersFinished(true);
      }
    };

    checkCompletion();
    const interval = setInterval(checkCompletion, 2000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [roomId]);

  return (
    <SafeAreaView
      style={styles.safeArea}
      edges={["top", "left", "right"]}
      data-oid="866.niu"
    >
      <View pointerEvents="none" style={styles.ambientOrbTop} />
      <View pointerEvents="none" style={styles.ambientOrbBottom} />
      <View pointerEvents="none" style={styles.sparkOne} />
      <View pointerEvents="none" style={styles.sparkTwo} />
      <View
        style={[
          tw`flex-1 items-center justify-center`,
          { paddingHorizontal: 20, paddingVertical: 24 },
        ]}
        data-oid="la.:zd5"
      >
        <View
          style={styles.heroCard}
        >
          <View style={styles.statusBadge}>
            <Ionicons name="checkmark-circle" size={14} color={IQES.goldSoft} />
            <Text style={styles.eyebrow}>
              {t('results.quizComplete', language)}
            </Text>
          </View>

          <View style={styles.lockBadge}>
            <Ionicons name="hourglass-outline" size={14} color={IQES.goldSoft} />
            <Text style={styles.eyebrow}>
              {t('multiplayer_waiting.waitingForPlayers', language)}
            </Text>
          </View>
          <Text style={styles.title}>
            {t('results.waitingForResults', language)}
          </Text>
          <Text style={styles.subtitle}>
            {t('multiplayer_waiting.resultsAlmostReady', language)}
          </Text>
          {roomContextLabel ? (
            <View style={styles.roomContextChip}>
              <Text style={styles.roomContextText}>
                {roomContextLabel}
              </Text>
            </View>
          ) : null}

          <View style={styles.scoreRow}>
            <View
              style={styles.scorePillPrimary}
            >
              <Text style={styles.scoreValueGold}>
                {score} / {totalQuestions}
              </Text>
              <Text style={styles.scoreLabel}>
                {t('multiplayer_waiting.correct', language)}
              </Text>
            </View>
            <View
              style={styles.scorePill}
            >
              <Text style={styles.scoreValue}>
                {percent}%
              </Text>
              <Text style={styles.scoreLabel}>
                {t('multiplayer_waiting.accuracy', language)}
              </Text>
            </View>
          </View>

          <View style={styles.loadingRail}>
            <ActivityIndicator
              size="small"
              color={IQES.gold}
              style={styles.spinnerInline}
              data-oid="gu9uke6"
            />
            <Text style={styles.loadingRailText}>
              {allPlayersFinished
                ? t('multiplayer_waiting.resultsAlmostReady', language)
                : t('multiplayer_waiting.othersStillAnswering', language)}
            </Text>
          </View>
        </View>
        <View style={styles.progressCard}>
          <View style={styles.progressHeader}>
            <View style={styles.progressIconWrap}>
              <Ionicons name="people-outline" size={18} color={IQES.goldSoft} />
            </View>
            <View style={styles.progressTextWrap}>
              <Text style={styles.progressTitle}>
                {t('multiplayer_waiting.playerProgress', language)}
              </Text>
              <Text style={styles.progressSubtitle}>
                {allPlayersFinished
                  ? t('multiplayer_waiting.resultsAlmostReady', language)
                  : t('multiplayer_waiting.othersStillAnswering', language)}
              </Text>
            </View>
          </View>
          <PlayerStatusList roomId={roomId} />
        </View>
        <TouchableOpacity
          style={[
            tw`mb-4 px-6 py-4 rounded-full`,
            styles.primaryButton,
          ]}
          onPress={onReviewAnswers}
          accessibilityLabel={t('multiplayer_waiting.reviewAnswers', language)}
          data-oid="r6.ud-6"
        >
          <Text style={styles.primaryButtonText} data-oid="be7zrku">
            {t('multiplayer_waiting.reviewAnswers', language)}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            tw`px-6 py-4 rounded-full border`,
            styles.secondaryButton,
          ]}
          onPress={onGoHome}
          accessibilityLabel={t('multiplayer_waiting.goHome', language)}
          data-oid="rjgle21"
        >
          <Text
            style={styles.secondaryButtonText}
            data-oid="y-xqxeq"
          >
            {t('multiplayer_waiting.goHome', language)}
          </Text>
        </TouchableOpacity>
        {isHost && (
          <TouchableOpacity
            style={[
              tw`mt-4 px-6 py-3 rounded-full`,
              styles.hostButton,
              { opacity: resetting ? 0.6 : 1 },
            ]}
            onPress={resetRoom}
            disabled={resetting}
            accessibilityLabel={t('multiplayer_waiting.resetRoom', language)}
            data-oid="xombwd6"
          >
            <Text style={styles.primaryButtonText} data-oid="y7:4_9t">
              {resetting ? t('multiplayer_waiting.resetting', language) : t('multiplayer_waiting.resetRoom', language)}
            </Text>
          </TouchableOpacity>
        )}
        {resetError && (
          <Text style={styles.errorText} data-oid="k:rkz92">
            {resetError}
          </Text>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: IQES.background,
  },
  ambientOrbTop: {
    position: "absolute",
    top: -120,
    right: -95,
    width: 240,
    height: 240,
    borderRadius: 120,
    backgroundColor: "rgba(91, 44, 131, 0.36)",
  },
  ambientOrbBottom: {
    position: "absolute",
    bottom: -150,
    left: -120,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: "rgba(248, 200, 78, 0.12)",
  },
  sparkOne: {
    position: "absolute",
    top: "29%",
    right: "19%",
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: IQES.goldSoft,
    opacity: 0.7,
  },
  sparkTwo: {
    position: "absolute",
    bottom: "24%",
    left: "26%",
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: IQES.gold,
    opacity: 0.55,
  },
  heroCard: {
    width: '100%',
    maxWidth: 360,
    paddingHorizontal: 24,
    paddingVertical: 28,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: IQES.borderStrong,
    alignItems: 'center',
    marginBottom: 24,
    backgroundColor: IQES.panel,
    shadowColor: IQES.gold,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 8,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: IQES.goldSoft,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    marginBottom: 12,
    backgroundColor: "rgba(248, 200, 78, 0.13)",
    borderWidth: 1,
    borderColor: IQES.border,
  },
  lockBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    marginBottom: 14,
    backgroundColor: "rgba(75, 31, 120, 0.58)",
    borderWidth: 1,
    borderColor: "rgba(255, 231, 138, 0.28)",
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 8,
    color: IQES.ivory,
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 14,
    color: IQES.muted,
  },
  roomContextChip: {
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginBottom: 18,
    backgroundColor: IQES.panelLift,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  roomContextText: {
    fontSize: 13,
    fontWeight: '700',
    color: IQES.text,
  },
  spinner: {
    marginBottom: 20,
  },
  spinnerInline: {
    marginRight: 8,
  },
  scoreRow: {
    width: '100%',
    flexDirection: 'row',
    gap: 12,
  },
  scorePill: {
    flex: 1,
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    backgroundColor: IQES.panelLift,
    borderWidth: 1,
    borderColor: "rgba(255, 247, 223, 0.12)",
  },
  scorePillPrimary: {
    flex: 1,
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    backgroundColor: "rgba(248, 200, 78, 0.13)",
    borderWidth: 1,
    borderColor: IQES.border,
  },
  scoreValue: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 4,
    color: IQES.ivory,
  },
  scoreValueGold: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 4,
    color: IQES.goldSoft,
  },
  scoreLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: IQES.muted,
  },
  progressCard: {
    width: '100%',
    maxWidth: 380,
    borderWidth: 1,
    borderRadius: 28,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 8,
    marginBottom: 20,
    backgroundColor: IQES.panelDeep,
    borderColor: IQES.border,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  progressIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    backgroundColor: IQES.purpleDeep,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  progressTextWrap: {
    flex: 1,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 2,
    color: IQES.ivory,
  },
  progressSubtitle: {
    fontSize: 14,
    lineHeight: 19,
    color: IQES.muted,
  },
  loadingRail: {
    width: '100%',
    marginTop: 16,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: "rgba(255, 247, 223, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(255, 247, 223, 0.1)",
  },
  loadingRailText: {
    fontSize: 14,
    fontWeight: '600',
    color: IQES.muted,
  },
  primaryButton: {
    minWidth: 260,
    backgroundColor: IQES.gold,
    borderWidth: 1,
    borderColor: IQES.goldSoft,
  },
  primaryButtonText: {
    color: IQES.purpleDeep,
    fontSize: 16,
    fontWeight: "800",
    textAlign: "center",
  },
  secondaryButton: {
    minWidth: 260,
    borderColor: IQES.border,
    backgroundColor: IQES.panelDeep,
  },
  secondaryButtonText: {
    color: IQES.goldSoft,
    fontSize: 16,
    fontWeight: "800",
    textAlign: "center",
  },
  hostButton: {
    backgroundColor: IQES.purple,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  errorText: {
    color: IQES.red,
    marginTop: 8,
  },
});

export default MultiplayerWaitingForResults;
